import { useMemo, type ReactNode } from "react";
import { MarkdownReact } from "@/components/MarkdownReact";
import { formatWeight } from "@/lib/utils";
import {
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
  getQuoteDisplayTotals,
} from "@/lib/quoteCurrency";
import {
  WEIGHT_UNIT_ABBREVIATION,
  type PublicQuote,
  type PublicQuoteItemLine,
  type PublicQuoteLine,
  type PublicQuoteQuantityBreak,
} from "@forgepoint/shared";
import { formatDateOnly } from "@forgepoint/shared";
import {
  computeLineNumbers,
  formatAddressLines,
  getQuoteEffectiveDate,
  publicQuotePeopleNames,
} from "../utils";
import {
  PdfPageViewer,
  usePdfPageInfo,
  type QuoteSection,
} from "../components/PdfPageViewer";

interface PublicQuoteTemplateFoundryQuoteProps {
  quote: PublicQuote;
  isExpired: boolean;
}

/** Shared column widths so the header and every per-line table line up. */
const TABLE_COLGROUP = (
  <colgroup>
    <col style={{ width: "7%" }} />
    <col style={{ width: "10%" }} />
    <col style={{ width: "41%" }} />
    <col style={{ width: "12%" }} />
    <col style={{ width: "15%" }} />
    <col style={{ width: "15%" }} />
  </colgroup>
);

// table-fixed forces every per-line table to honor the colgroup widths so the
// vertical rules line up across rows instead of jagging when content varies.
const TABLE_CLASS = "w-full border-collapse text-xs table-fixed";
/** Item cells draw only vertical rules so rows have no lines between them. */
const CELL_CLASS = "border-x border-gray-900 px-2 py-1 align-top";
/** Header/subtotal cells draw a full box border. */
const BOX_CELL_CLASS = "border border-gray-900 px-2 py-1 align-top";

/**
 * Formats a date as a long US date string, e.g. "May 25, 2026".
 * @param value - The date value to format
 * @returns The formatted date, or null for missing/invalid values
 */
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "long",
  day: "numeric",
  year: "numeric",
};

function formatDate(value?: Date | string | null): string | null {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    return formatDateOnly(value, DATE_FORMAT_OPTIONS, "en-US");
  }
  if (Number.isNaN(value.getTime())) {
    return null;
  }
  return value.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
}

/**
 * Looks up a quote-level custom field value by name, ignoring case.
 * @param quote - The hydrated quote data
 * @param key - The custom field name to match case-insensitively
 * @returns The trimmed string value, or null when unset
 */
function getCustomField(quote: PublicQuote, key: string): string | null {
  const fields = quote.customFields;
  if (!fields) {
    return null;
  }
  const lowerKey = key.toLowerCase();
  const matchingKey = Object.keys(fields).find(
    (k) => k.toLowerCase() === lowerKey,
  );
  const value = matchingKey ? fields[matchingKey] : undefined;
  if (value === null || value === undefined) {
    return null;
  }
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  return text.trim() || null;
}

/**
 * Foundry Quotation public quote template.
 * Reproduces a letterhead quotation with a boxed item/spec table, per-each
 * weights and prices, commercial terms, and a repeated price-revision footer,
 * followed by the terms and conditions. Item lines are emitted as small,
 * individually paginated sections so long quotes fill each page top-to-bottom
 * and export (print) matches the on-screen layout exactly.
 * @param quote - The hydrated quote data
 * @param isExpired - Whether the quote has expired
 */
export function PublicQuoteTemplateFoundryQuote({
  quote,
}: PublicQuoteTemplateFoundryQuoteProps) {
  const { sections, repeatableHeader, repeatableFooter } =
    useFoundryQuoteSections(quote);
  return (
    <PdfPageViewer
      sections={sections}
      repeatableHeader={repeatableHeader}
      repeatableFooter={repeatableFooter}
      watermark={quote.watermark}
    />
  );
}

interface FoundryQuoteLayout {
  sections: QuoteSection[];
  repeatableHeader: ReactNode;
  repeatableFooter: ReactNode;
}

/**
 * Builds the pageable sections, repeated table header, and footer for the
 * Foundry Quotation layout. The masthead (letterhead, info block, intro) opens
 * page one with its own inline column header; continuation pages repeat only
 * the column header.
 * @param quote - The hydrated quote data
 * @returns Sections and repeatable header/footer for PdfPageViewer
 */
function useFoundryQuoteSections(quote: PublicQuote): FoundryQuoteLayout {
  const lines = useMemo(() => quote.lines ?? [], [quote.lines]);
  const lineNumbers = useMemo(() => computeLineNumbers(lines), [lines]);
  const displayTotals = getQuoteDisplayTotals(quote);

  const repeatableHeader = <ColumnHeader />;

  const repeatableFooter = quote.quoteFooter ? (
    <p className="pt-2 text-[9px] font-bold text-gray-900 leading-snug">
      {quote.quoteFooter}
    </p>
  ) : null;

  const sections: QuoteSection[] = [];

  // Page one opens with the full masthead and its own column header, so the
  // repeatable header is suppressed here and shown on continuation pages only.
  sections.push({
    key: "masthead",
    skipHeader: true,
    content: (
      <div className="text-gray-900">
        <Letterhead quote={quote} />
        <InfoBlock quote={quote} />
        <p className="mb-3 text-xs">
          We are pleased to submit our quotation, subject to{" "}
          <span className="italic">the Terms and Conditions of Sale</span>{" "}
          (shown on the last page)
        </p>
        <table className={TABLE_CLASS}>
          {TABLE_COLGROUP}
          <ColumnHeaderRow />
        </table>
      </div>
    ),
  });

  lines.forEach((line, index) => {
    const rows = renderLineRows(line, lineNumbers[index], quote);
    if (!rows) {
      return;
    }
    sections.push({
      key: `line-${line.id || index}`,
      sealBottom: true,
      content: (
        <table className={TABLE_CLASS}>
          {TABLE_COLGROUP}
          {rows}
        </table>
      ),
    });
  });

  // Always emit a closing row so the item box seals at the bottom, showing the
  // Sub-Total when totals are enabled and a plain border otherwise.
  sections.push({
    key: "table-close",
    content: (
      <table className={TABLE_CLASS}>
        {TABLE_COLGROUP}
        <tbody>
          <tr>
            {/* Empty left cells keep the vertical column rules running down to
                the subtotal instead of a single merged cell breaking them. */}
            <td className={`${CELL_CLASS} border-b`} />
            <td className={`${CELL_CLASS} border-b`} />
            <td className={`${CELL_CLASS} border-b`} />
            <td className={`${CELL_CLASS} border-b`} />
            {quote.showTotals ? (
              <>
                <td className={`${CELL_CLASS} border-b text-right font-bold`}>
                  Sub-Total
                </td>
                <td
                  className={`${CELL_CLASS} border-b text-right font-bold whitespace-nowrap`}
                >
                  {displayTotals.subtotalWithDiscountsFormatted}
                </td>
              </>
            ) : (
              <>
                <td className={`${CELL_CLASS} border-b`} />
                <td className={`${CELL_CLASS} border-b`} />
              </>
            )}
          </tr>
        </tbody>
      </table>
    ),
  });

  sections.push({
    key: "commercial-terms",
    content: <CommercialTerms quote={quote} />,
  });

  if (quote.termsOfService) {
    sections.push({
      key: "terms",
      forcePageBreak: true,
      skipHeader: true,
      content: (
        <div>
          <h2 className="mb-3 text-center text-sm font-bold text-gray-900 underline">
            General Terms and Conditions of Sale
          </h2>
          <div className="prose prose-xs prose-slate max-w-none text-justify text-[10px] leading-snug text-gray-900 [&_li::marker]:font-bold">
            <MarkdownReact>{quote.termsOfService}</MarkdownReact>
          </div>
        </div>
      ),
    });
  }

  return { sections, repeatableHeader, repeatableFooter };
}

/** The boxed column header as a standalone table (used on continuation pages). */
function ColumnHeader() {
  return (
    <table className={TABLE_CLASS}>
      {TABLE_COLGROUP}
      <ColumnHeaderRow />
    </table>
  );
}

/** The ITEM/QUANTITY/DESCRIPTION/WT/PRICE/TOTAL header row. */
function ColumnHeaderRow() {
  return (
    <thead>
      <tr className="text-[10px] font-bold uppercase">
        <th className={`${BOX_CELL_CLASS} text-center`}>Item</th>
        <th className={`${BOX_CELL_CLASS} text-center`}>Quantity</th>
        <th className={`${BOX_CELL_CLASS} text-left`}>
          Description and Specifications
        </th>
        <th className={`${BOX_CELL_CLASS} text-center`}>
          Wt. <span className="normal-case">(ea.)</span>
        </th>
        <th className={`${BOX_CELL_CLASS} text-center`}>
          Price <span className="normal-case">(ea.)</span>
        </th>
        <th className={`${BOX_CELL_CLASS} text-center`}>Total</th>
      </tr>
    </thead>
  );
}

/** Company letterhead: logo, address block, and the "Quotation" title. */
function Letterhead({ quote }: { quote: PublicQuote }) {
  const { pageNumber, totalPages } = usePdfPageInfo();
  const orgAddressLines = formatAddressLines(quote.organization?.address);
  const contactParts = [
    quote.organization?.phone && `Tel: ${quote.organization.phone}`,
    quote.organization?.fax && `Fax: ${quote.organization.fax}`,
  ].filter(Boolean);

  return (
    <div className="mb-6 flex items-start justify-between">
      <div className="flex items-start gap-3">
        {quote.organization?.logoUrl && (
          <img
            src={quote.organization.logoUrl}
            alt={`${quote.organization.name} logo`}
            className="h-16 w-auto object-contain"
          />
        )}
        <div className="text-[10px] leading-tight text-gray-900">
          {orgAddressLines?.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {contactParts.length > 0 && (
            <p className="font-semibold">{contactParts.join(" · ")}</p>
          )}
          {quote.organization?.website && (
            <p>Website: {quote.organization.website}</p>
          )}
        </div>
      </div>
      <div className="text-right">
        <p className="text-2xl font-bold text-blue-600">Quotation</p>
        <p className="text-[10px] text-gray-600">
          Page {pageNumber} of {totalPages} Page(s)
        </p>
      </div>
    </div>
  );
}

/** Two-column To/Attention and Date/Quotation No./Reference/Copy To block. */
function InfoBlock({ quote }: { quote: PublicQuote }) {
  const rows: Array<[string, ReactNode]> = [
    ["To:", quote.customerCompany?.name ?? null],
    [
      "Attention:",
      getCustomField(quote, "Attention") || publicQuotePeopleNames(quote),
    ],
  ];
  const metaRows: Array<[string, ReactNode]> = [
    ["Date:", formatDate(getQuoteEffectiveDate(quote))],
    ["Quotation No.:", quote.quoteDisplayNumber],
    ["Your Reference:", getCustomField(quote, "Your Reference")],
    ["Copy To:", getCustomField(quote, "Copy To")],
  ];

  return (
    <div className="mb-4 grid grid-cols-2 gap-x-8 text-xs">
      <InfoRows rows={rows} />
      <InfoRows rows={metaRows} />
    </div>
  );
}

/** Renders label/value rows for the info block, keeping empty values blank. */
function InfoRows({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <table className="border-collapse">
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}>
            <td className="whitespace-nowrap pr-3 align-top font-bold">
              {label}
            </td>
            <td className="align-top">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * Renders one quote line as a table body, dispatching on line type.
 * @param line - The quote line to render
 * @param lineNumber - The display line number, if any
 * @param quote - The hydrated quote (for currency formatting)
 * @returns A tbody element for the line, or null when it renders nothing
 */
function renderLineRows(
  line: PublicQuoteLine,
  lineNumber: number | null,
  quote: PublicQuote,
): ReactNode {
  if (line.type === "SECTION") {
    if (!line.description) {
      return null;
    }
    return (
      <tbody>
        <tr>
          <td className={`${CELL_CLASS} font-bold uppercase`} colSpan={6}>
            {line.description}
          </td>
        </tr>
      </tbody>
    );
  }

  if (line.type === "NOTE") {
    if (!line.description) {
      return null;
    }
    return (
      <tbody>
        <tr>
          <td className={CELL_CLASS} />
          <td className={CELL_CLASS} />
          <td className={CELL_CLASS}>
            <div
              className="prose prose-sm max-w-none whitespace-pre-line break-words text-gray-900"
              style={{ fontSize: "11px", lineHeight: 1.4 }}
            >
              <MarkdownReact>{line.description}</MarkdownReact>
            </div>
          </td>
          <td className={CELL_CLASS} />
          <td className={CELL_CLASS} />
          <td className={CELL_CLASS} />
        </tr>
      </tbody>
    );
  }

  if (line.type === "DISCOUNT") {
    if (!quote.showTotals) {
      return null;
    }
    const discount =
      line.amount !== null ? formatQuoteLineTotal(line, quote) : null;
    return (
      <tbody>
        <tr>
          <td className={CELL_CLASS} />
          <td className={CELL_CLASS} />
          <td className={CELL_CLASS} />
          <td className={CELL_CLASS} />
          <td className={`${CELL_CLASS} text-right font-semibold`}>
            {line.description || "Discount"}
          </td>
          <td className={`${CELL_CLASS} text-right font-semibold`}>
            {discount ? `-${discount}` : ""}
          </td>
        </tr>
      </tbody>
    );
  }

  return <ItemRows line={line} lineNumber={lineNumber} quote={quote} />;
}

/** Renders an ITEM line: name + specs, quantity, per-each weight and price. */
function ItemRows({
  line,
  lineNumber,
  quote,
}: {
  line: PublicQuoteItemLine;
  lineNumber: number | null;
  quote: PublicQuote;
}) {
  const itemName = line.item?.name ?? line.description ?? "Item";
  const specText =
    line.description && line.description !== itemName
      ? line.description
      : (line.item?.description ?? null);
  const weight = formatItemWeight(line);

  return (
    <tbody>
      {line.quantityBreaks.map((qb, qbIndex) => {
        const quantity = qb.quantity?.trim();
        const unitPrice = qb.unitPriceOverride ?? qb.unitPrice;
        return (
          <tr key={`qb-${qbIndex}`}>
            <td className={`${CELL_CLASS} text-center`}>
              {qbIndex === 0 ? lineNumber : ""}
            </td>
            <td className={`${CELL_CLASS} text-center`}>
              {quantity && !Number.isNaN(Number(quantity))
                ? new Intl.NumberFormat("en-US").format(Number(quantity))
                : (quantity ?? "")}
            </td>
            <td className={CELL_CLASS}>
              {qbIndex === 0 && (
                <>
                  <p className="font-bold underline">{itemName}</p>
                  {specText && (
                    <div className="whitespace-pre-line text-gray-900">
                      {specText}
                    </div>
                  )}
                </>
              )}
            </td>
            <td className={`${CELL_CLASS} text-center`}>
              {qbIndex === 0 ? weight : ""}
            </td>
            <td className={`${CELL_CLASS} text-right whitespace-nowrap`}>
              {unitPrice ? formatQuoteUnitPrice(qb, quote) : ""}
            </td>
            <td className={`${CELL_CLASS} text-right whitespace-nowrap`}>
              {formatExtendedPrice(qb, quote)}
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}

/**
 * Formats a quantity break's backend-computed extended price in the quote's
 * display currency, or empty when the backend computed no amount.
 * @param qb - The quantity break carrying the backend amounts
 * @param quote - The hydrated quote (currency fields)
 * @returns The formatted extended price, or empty string
 */
function formatExtendedPrice(
  qb: PublicQuoteQuantityBreak,
  quote: PublicQuote,
): string {
  if (!qb.amount) {
    return "";
  }
  return formatQuoteLineTotal(qb, quote);
}

/**
 * Formats an item's per-each weight with its unit abbreviation, e.g. "1050 lb".
 * @param line - The hydrated item line
 * @returns The formatted per-each weight, or empty string
 */
function formatItemWeight(line: PublicQuoteItemLine): string {
  const weight = line.item?.weight;
  const unit = line.item?.weightUnit;
  if (!weight) {
    return "";
  }
  const formatted = formatWeight(weight);
  return unit ? `${formatted} ${WEIGHT_UNIT_ABBREVIATION[unit]}` : formatted;
}

/** Commercial terms footer: freight, taxes, payment, delivery, and signature. */
function CommercialTerms({ quote }: { quote: PublicQuote }) {
  const minimumOrder = getCustomField(quote, "Minimum Order");
  const rows: Array<[string, ReactNode]> = [
    ["Freight:", getCustomField(quote, "Freight")],
    ["Sales Taxes:", getCustomField(quote, "Sales Taxes")],
    ["Terms of payment:", quote.paymentTerms],
    ["Delivery:", quote.deliveryTerms],
  ];

  return (
    <div className="mt-4 flex items-start justify-between text-xs">
      <div>
        <table className="border-collapse">
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label}>
                <td className="whitespace-nowrap pr-3 align-top text-gray-700">
                  {label}
                </td>
                <td className="align-top text-gray-900 underline">
                  {value || <>&nbsp;</>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {minimumOrder && (
          <p className="mt-3 text-gray-900">Minimum order of {minimumOrder}</p>
        )}
      </div>
      <div className="min-w-[220px] text-gray-900">
        <div className="flex items-end gap-2">
          <span className="text-gray-700">Per:</span>
          <span className="flex-1 border-b border-gray-900">&nbsp;</span>
        </div>
        {quote.salesRepName && <p className="mt-1">{quote.salesRepName}</p>}
        <p className="font-bold text-blue-600">{quote.organization?.name}</p>
        {quote.isSigned && quote.signature && (
          <SignedBlock
            name={quote.signature.name}
            signedAt={quote.signature.signedAt}
          />
        )}
      </div>
    </div>
  );
}

/** Signature confirmation block for already-signed quotes. */
function SignedBlock({
  name,
  signedAt,
}: {
  name: string;
  signedAt: Date | string;
}) {
  return (
    <div className="mt-4 border-t border-gray-300 pt-2">
      <p className="text-gray-600">Accepted by:</p>
      <p className="font-signature text-xl">{name}</p>
      <p className="text-gray-600">
        Signed {new Date(signedAt).toLocaleDateString()}
      </p>
    </div>
  );
}
